import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuthReady } from '@/hooks/useAuthReady';
import { useOnboardingChecklist } from '@/hooks/useOnboardingChecklist';
import { IntroSlideshow } from './IntroSlideshow';
import { TourCenter } from './TourCenter';

/**
 * Central controller for the "first sign-in" journey. Mounted once inside
 * AuthGuard. Decides whether to show:
 *   1. IntroSlideshow (once per user, tracked via onboarding_checklist.intro_seen_at)
 *   2. Redirect to /welcome (once the intro is dismissed, if the checklist
 *      isn't completed/dismissed yet)
 *   3. On slideshow close: if a sample project exists, kick off the
 *      FirstProjectTour and pulse the ChatSidebar help button so users
 *      discover the per-area tours.
 */
export function OnboardingOrchestrator() {
  const { user, isReady } = useAuthReady();
  const { state, loading, markIntroSeen, isComplete } = useOnboardingChecklist();
  const navigate = useNavigate();
  const location = useLocation();

  const [showIntro, setShowIntro] = useState(false);
  const decidedRef = useRef(false);

  // Decide once per mount, after auth + checklist are ready.
  useEffect(() => {
    if (!isReady || !user || loading || decidedRef.current) return;
    decidedRef.current = true;

    const introSeen = !!state?.intro_seen_at;

    if (!introSeen) {
      setShowIntro(true);
      return;
    }

    // Intro already seen — fall through to the existing welcome redirect
    // (only if the checklist itself isn't finished, and we're not already
    // on /welcome or a public-ish page).
    if (!isComplete && location.pathname.startsWith('/app')) {
      navigate('/welcome', { replace: true });
    }
  }, [isReady, user, loading, state, isComplete, navigate, location.pathname]);

  const handleClose = async () => {
    setShowIntro(false);
    await markIntroSeen();

    // Check for a sample project so we can auto-chain the FirstProjectTour.
    let hasSample = false;
    try {
      const { data } = await supabase
        .from('projects')
        .select('id')
        .eq('is_sample', true)
        .limit(1)
        .maybeSingle();
      hasSample = !!data;
    } catch { /* noop */ }

    if (hasSample) {
      // Ensure we're in the app, then trigger the sample tour + help pulse.
      if (!location.pathname.startsWith('/app')) {
        navigate('/app', { replace: true });
      }
      // Wait a beat so the Gantt + chat targets mount.
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('mfp:start-tour'));
      }, 800);
      // Pulse the ChatSidebar help button a moment after the tour begins.
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('onboarding:pulse-help'));
      }, 1400);
    } else if (!isComplete) {
      navigate('/welcome', { replace: true });
    }
  };

  return (
    <>
      <IntroSlideshow open={showIntro} onClose={handleClose} />
      {/* Global Tour Center mount so `openTourCenter()` works from any route,
          including /welcome where the ChatSidebar isn't rendered. Hidden
          trigger — opens via the `tour-center:open` window event. */}
      <TourCenter trigger={<span className="hidden" aria-hidden="true" />} />
    </>
  );
}
